import type { HullDefinition } from "@frontier/content/definitions";
import type { MovementRules } from "@frontier/content/combat-rules";
import { MOVEMENT_RULES } from "@frontier/content/movement-rules";
import type { Obstacle, Point } from "./model.js";
import { rotateTowards } from "./geometry.js";
import { INTRO_TRANSPORT_ID } from "@frontier/content/tutorial";
import { TRAINING } from "@frontier/content/training";
import { REGIONS, WORLD_LAYOUT } from "@frontier/content/contracts";

export interface MovementBody extends Point {
  angle: number;
  radius: number;
}
export interface MovementObstacle extends Obstacle {
  solid?: boolean;
}
interface MovementShip extends Point {
  id: string;
  radius: number;
  dead: boolean;
  kind?: string;
  docked?: boolean;
  archetype?: string;
}
export interface MovementOptions {
  rules?: MovementRules;
  mx: number;
  my: number;
  desiredAngle: number;
  boost: boolean;
  boostSpeedFactor?: number;
  dt: number;
  hull: Pick<HullDefinition, "speed" | "turnRate">;
  obstacles: MovementObstacle[];
  width: number;
  height: number;
  bounds?: { x: number; y: number; width: number; height: number };
}

export function movementRegionBounds(point: Point): { x: number; y: number; width: number; height: number } {
  return (
    REGIONS.find(
      (region) =>
        point.x >= region.x &&
        point.x < region.x + region.width &&
        point.y >= region.y &&
        point.y <= region.y + region.height,
    ) ?? { x: 0, y: 0, width: WORLD_LAYOUT.width, height: WORLD_LAYOUT.height }
  );
}

/** Boost cannot recharge itself while its control is held. Use the same fixed dt on both sides. */
export function updateMovementEnergy(
  energy: number,
  maxEnergy: number,
  boostIntent: boolean,
  movementLength: number,
  dt: number,
  boostEnergyFactor = 1,
  rules: MovementRules = MOVEMENT_RULES,
): { energy: number; boosting: boolean } {
  const cost = rules.boostEnergyPerSecond * boostEnergyFactor * dt;
  const boosting = boostIntent && movementLength > 0 && energy >= cost;
  if (boosting) return { energy: energy - cost, boosting: true };
  if (boostIntent) return { energy, boosting: false };
  return { energy: Math.min(maxEnergy, energy + rules.energyPerSecond * dt), boosting: false };
}

export function introMovementObstacles(
  base: Obstacle[],
  ships: Iterable<MovementShip>,
  intro: boolean,
  ownId?: string,
): Obstacle[] {
  if (!intro) return base;
  const obstacles = [...base];
  for (const ship of ships) {
    const peer = ownId !== undefined && ship.kind === "pilot" && ship.id !== ownId && !ship.docked;
    if (!ship.dead && (ship.id === INTRO_TRANSPORT_ID || ship.id === TRAINING.transportId || peer))
      obstacles.push({ id: ship.id, x: ship.x, y: ship.y, radius: ship.radius });
  }
  return obstacles;
}

export function pilotMovementObstacles(
  base: Obstacle[],
  ships: Iterable<MovementShip>,
  intro: boolean,
  ownId: string,
): MovementObstacle[] {
  const bodies = [...ships];
  const obstacles: MovementObstacle[] = [...introMovementObstacles(base, bodies, intro, ownId)];
  for (const ship of bodies)
    if (ship.archetype === "boss" && ship.id !== ownId && !ship.dead && !ship.docked)
      obstacles.push({ id: ship.id, x: ship.x, y: ship.y, radius: ship.radius, solid: true });
  return obstacles;
}

function positionIsFree(point: Point, body: MovementBody, options: MovementOptions): boolean {
  const bounds = options.bounds ?? { x: 0, y: 0, width: options.width, height: options.height };
  return (
    point.x >= bounds.x + body.radius &&
    point.x <= bounds.x + bounds.width - body.radius &&
    point.y >= bounds.y + body.radius &&
    point.y <= bounds.y + bounds.height - body.radius &&
    options.obstacles.every(
      (obstacle) =>
        Math.hypot(point.x - obstacle.x, point.y - obstacle.y) >= body.radius + obstacle.radius - 1e-7,
    )
  );
}

function resolveSolidObstacles(body: MovementBody, options: MovementOptions): void {
  for (const obstacle of options.obstacles) {
    if (
      !obstacle.solid ||
      Math.hypot(body.x - obstacle.x, body.y - obstacle.y) >= body.radius + obstacle.radius
    )
      continue;
    const dx = body.x - obstacle.x;
    const dy = body.y - obstacle.y;
    const angle =
      dx || dy
        ? Math.atan2(dy, dx)
        : Math.atan2(options.height / 2 - obstacle.y, options.width / 2 - obstacle.x);
    // Try the nearest radial exit first, then a bounded deterministic search around blocked exits.
    for (let ring = 0; ring < 8; ring++) {
      const radius = body.radius + obstacle.radius + 1e-6 + ring * Math.max(16, body.radius * 2);
      for (let index = 0; index < 16; index++) {
        const offset = (Math.ceil(index / 2) * (index % 2 === 0 ? -1 : 1) * Math.PI) / 8;
        const point = {
          x: obstacle.x + Math.cos(angle + offset) * radius,
          y: obstacle.y + Math.sin(angle + offset) * radius,
        };
        if (!positionIsFree(point, body, options)) continue;
        body.x = point.x;
        body.y = point.y;
        return;
      }
    }
  }
}

function resolveObstacles(body: MovementBody, options: MovementOptions): void {
  for (const obstacle of options.obstacles) {
    if (obstacle.solid) continue;
    const dx = body.x - obstacle.x;
    const dy = body.y - obstacle.y;
    const minimum = body.radius + obstacle.radius;
    if (Math.abs(dx) >= minimum || Math.abs(dy) >= minimum) continue;
    const lengthToRock = Math.hypot(dx, dy);
    if (lengthToRock >= minimum) continue;
    const nx = lengthToRock > 0 ? dx / lengthToRock : 1;
    const ny = lengthToRock > 0 ? dy / lengthToRock : 0;
    body.x = obstacle.x + nx * minimum;
    body.y = obstacle.y + ny * minimum;
  }
  const bounds = options.bounds ?? { x: 0, y: 0, width: options.width, height: options.height };
  body.x = Math.max(bounds.x + body.radius, Math.min(bounds.x + bounds.width - body.radius, body.x));
  body.y = Math.max(bounds.y + body.radius, Math.min(bounds.y + bounds.height - body.radius, body.y));
  resolveSolidObstacles(body, options);
}

/** Returns controlled distance after collisions, excluding an initial overlap correction. */
export function integrateMovement(body: MovementBody, options: MovementOptions): number {
  const { hull, dt } = options;
  const rules = options.rules ?? MOVEMENT_RULES;
  resolveObstacles(body, options);
  const previousX = body.x;
  const previousY = body.y;
  body.angle = rotateTowards(body.angle, options.desiredAngle, hull.turnRate * dt);
  const length = Math.hypot(options.mx, options.my);
  const mx = options.mx / Math.max(1, length);
  const my = options.my / Math.max(1, length);
  const forward =
    length > 0 ? (mx * Math.cos(body.angle) + my * Math.sin(body.angle)) / Math.min(1, length) : 0;
  const directional = forward > 0 ? rules.forwardBase + rules.forwardGain * forward : rules.reverseFactor;
  const speed = hull.speed * directional * (options.boost ? rules.boostSpeedFactor * (options.boostSpeedFactor ?? 1) : 1);
  body.x += mx * speed * dt;
  body.y += my * speed * dt;
  resolveObstacles(body, options);
  return Math.hypot(body.x - previousX, body.y - previousY);
}
